import { WAYPOINT_ALIASES } from './radiotelephony.js';
/** Deterministic transcript assessment. This does not assess acoustic pronunciation. Small speech-recognition slips that line up with the reference wording are read as the reference words (see reconcileSlips). */
export const RUBRIC_VERSION = 'template-critical-fields-order-v5';

const DIGITS = { zero: '0', oh: '0', one: '1', two: '2', three: '3', tree: '3', four: '4', fower: '4', five: '5', fife: '5', six: '6', seven: '7', eight: '8', nine: '9', niner: '9' };
const TENS = { ten: 10, eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90 };
const NATO = { alpha: 'a', alfa: 'a', bravo: 'b', charlie: 'c', delta: 'd', echo: 'e', foxtrot: 'f', golf: 'g', hotel: 'h', india: 'i', juliet: 'j', juliett: 'j', kilo: 'k', lima: 'l', mike: 'm', november: 'n', oscar: 'o', papa: 'p', quebec: 'q', romeo: 'r', sierra: 's', tango: 't', uniform: 'u', victor: 'v', whiskey: 'w', whisky: 'w', xray: 'x', yankee: 'y', zulu: 'z' };
const NEGATIVES = new Set(['not', 'no', 'never', 'negative', 'unable', 'cannot', 'cant', 'dont', 'doesnt', 'didnt', 'isnt', 'arent', 'wasnt', 'werent', 'wont', 'wouldnt', 'shouldnt', 'couldnt', 'disregard', 'cancel', 'cancelled', 'canceled', 'denied', 'revoke', 'revoked', 'rescinded', 'withdrawn', 'prohibited']);
/** Hesitation sounds carry no radio meaning; they are dropped before any check. */
const FILLERS = new Set(['uh', 'uhh', 'uhm', 'um', 'umm', 'er', 'erm', 'ah', 'eh', 'hm', 'hmm', 'mm', 'mmm']);
const SIDE_WORDS = new Set(['left', 'right', 'center']);

/** Without hundred or thousand, a digit word before a tens word is read as concatenated digits: one twenty three → 123, two seventy → 270. */
function digitsRun(tokens) {
  const out = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const tens = Object.hasOwn(TENS, tokens[i + 1]) ? TENS[tokens[i + 1]] : 0;
    if (!Object.hasOwn(DIGITS, tokens[i]) || tens < 20 || tens % 10) out.push(tokens[i]);
    else if (Object.hasOwn(DIGITS, tokens[i + 2])) { out.push(`${DIGITS[tokens[i]]}${tens / 10}${DIGITS[tokens[i + 2]]}`); i += 2; }
    else { out.push(`${DIGITS[tokens[i]]}${tens / 10}0`); i += 1; }
  }
  return out;
}

function numberPart(tokens) {
  if (!tokens.includes('hundred') && !tokens.includes('thousand')) tokens = digitsRun(tokens);
  if (!tokens.some((token) => token in TENS || token === 'hundred' || token === 'thousand')) {
    return tokens.map((token) => DIGITS[token] ?? token).join('');
  }
  const at = tokens.indexOf('thousand');
  if (at > 0 && tokens.slice(0, at).every((token) => /^\d$/.test(token) || Object.prototype.hasOwnProperty.call(DIGITS, token))) return String(Number(numberPart(tokens.slice(0, at))) * 1000 + Number(numberPart(tokens.slice(at + 1))));
  let total = 0;
  let group = 0;
  for (const token of tokens) {
    if (token === 'hundred') group = (group || 1) * 100;
    else if (token === 'thousand') { total += (group || 1) * 1000; group = 0; }
    else group += Number(DIGITS[token] ?? TENS[token] ?? token);
  }
  return String(total + group);
}

function numericWord(token) {
  return /^\d+(?:\.\d+)?$/.test(token) || token in DIGITS || token in TENS || token === 'hundred' || token === 'thousand';
}
/** "and" joins a run only between hundred/thousand and another number: one hundred and one, two thousand and fifty. */
function continuesNumber(words, i) {
  return numericWord(words[i]) || ['point', 'decimal'].includes(words[i]) || (words[i] === 'and' && ['hundred', 'thousand'].includes(words[i - 1]) && numericWord(words[i + 1]));
}

/** Runway numbers and sides, and heading values, read from their neighbouring words. Safe to run again after slips are rewritten. */
function numberContext(tokens) {
  for (let i = 0; i < tokens.length; i += 1) {
    if (/^\d{1,2}$/.test(tokens[i]) && (tokens[i - 1] === 'runway' || ['left', 'right', 'center', 'centre', 'l', 'r', 'c'].includes(tokens[i + 1]))) {
      tokens[i] = tokens[i].padStart(2, '0');
      if (tokens[i + 1] === 'l') tokens[i + 1] = 'left';
      if (tokens[i + 1] === 'r') tokens[i + 1] = 'right';
      if (tokens[i + 1] === 'c' || tokens[i + 1] === 'centre') tokens[i + 1] = 'center';
      if (tokens[i - 1] === 'runway' && ['see', 'sea'].includes(tokens[i + 1])) tokens[i + 1] = 'center';
    }
    if (tokens[i - 1] === 'heading' && /^\d+$/.test(tokens[i])) tokens[i] = String(Number(tokens[i]));
  }
  return tokens;
}

/** Exported for diagnostics and reproducible saved evaluation evidence. */
export function normalizeTranscript(text) {
  const words = (String(text ?? '').toLowerCase().replace(/\byou[’']re\b/g, 'you are').replace(/[’']/g, '').replace(/take[\s-]+off/g, 'takeoff').replace(/\bstar[\s-]*walker\b/g, 'starwalker').replace(/\bfull[\s-]?stop\b/g, 'full stop').replace(/\bdownwind\s+for\s*(?=[.。!?])/g, 'downwind for full stop').replace(/\bcentre\b/g, 'center').replace(/\bx[\s-]+ray\b/g, 'xray').match(/[a-z]+|\d+(?:\.\d+)?/g) ?? []).filter((word) => !FILLERS.has(word));
  const tokens = [];
  for (let i = 0; i < words.length; i += 1) {
    if (numericWord(words[i])) {
      const parts = [];
      while (i < words.length && continuesNumber(words, i)) { if (words[i] !== 'and') parts.push(words[i]); i += 1; }
      i -= 1;
      const decimal = parts.findIndex((token) => ['point', 'decimal'].includes(token));
      tokens.push(decimal < 0 ? numberPart(parts) : `${numberPart(parts.slice(0, decimal))}.${numberPart(parts.slice(decimal + 1))}`);
    } else if (words[i] === 'cessna' && /^[a-z]{2,4}$/.test(words[i + 1] ?? '') && !(words[i + 1] in NATO)) {
      tokens.push('cessna', ...words[++i].split(''));
    } else {
      tokens.push(NATO[words[i]] ?? words[i]);
    }
  }
  return numberContext(tokens);
}

const WAYPOINT_SEQUENCES = WAYPOINT_ALIASES.flatMap((choice) => choice.aliases.map((alias) => ({ key: choice.key, tokens: normalizeTranscript(alias) }))).filter((choice) => choice.tokens.length).sort((a, b) => b.tokens.length - a.tokens.length);
function waypointKey(value) {
  const text = normalizeTranscript(value).join(' ');
  return WAYPOINT_SEQUENCES.find((choice) => choice.tokens.join(' ') === text)?.key ?? text;
}
function canonical(tokens) {
  const out = [];
  for (let i = 0; i < tokens.length;) {
    const choice = WAYPOINT_SEQUENCES.find((sequence) => sequence.tokens.every((token, offset) => tokens[i + offset] === token));
    if (choice) { out.push(choice.key); i += choice.tokens.length; } else out.push(tokens[i++]);
  }
  return out;
}
function findSequence(tokens, sequence) {
  return tokens.findIndex((_, index) => sequence.every((token, offset) => tokens[index + offset] === token));
}

/** Edit distance that counts one swap of neighbouring letters as a single edit (optimal string alignment). */
function editDistance(a, b) {
  const rows = Array.from({ length: a.length + 1 }, (_, i) => [i, ...new Array(b.length).fill(0)]);
  for (let j = 1; j <= b.length; j += 1) rows[0][j] = j;
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      rows[i][j] = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + 1);
    }
  }
  return rows[a.length][b.length];
}
/** Letters a heard word may differ by: none below 4 letters, 1 for 4, 2 for 5–8, 3 for 9 or more. */
const slipAllowance = (length) => (length >= 9 ? 3 : length >= 5 ? 2 : length === 4 ? 1 : 0);
const closeTo = (heard, word) => editDistance(heard, word) <= slipAllowance(word.length);
/** Numbers, heard side words and negatives are never rewritten; a slip becomes a side word only where the reference says one. */
const slipSource = (token) => /^[a-z]+$/.test(token) && !SIDE_WORDS.has(token) && !NEGATIVES.has(token);
const slipWord = (token) => /^[a-z]{4,}$/.test(token) && !NEGATIVES.has(token);
const splitWord = (token) => /^[a-z]{2,}$/.test(token) && !SIDE_WORDS.has(token) && !NEGATIVES.has(token);
const spelling = (letter) => Object.keys(NATO).find((name) => NATO[name] === letter);
/** A misheard NATO word counts as the expected letter only when no other letter's word is as close. */
function closeToLetter(heard, letter) {
  if (heard.length < 3 || !spelling(letter)) return false;
  let own = Infinity;
  let other = Infinity;
  for (const [name, value] of Object.entries(NATO)) {
    const distance = editDistance(heard, name);
    if (value !== letter) other = Math.min(other, distance);
    else if (distance <= slipAllowance(name.length)) own = Math.min(own, distance);
  }
  return own < other;
}
const SLIP_COST = 10;
/**
 * Speech-recognition slips. Aligns heard tokens with the reference wording, then rewrites a heard word that lines up with a
 * reference word and differs by a few letters, two or three heard words that join into one reference word (starwalk up →
 * starwalker), or one heard word that splits into two reference words (panpan → pan pan). Unaligned words stay as heard.
 */
export function reconcileSlips(tokens, reference) {
  const heard = [...tokens];
  if (!heard.length || !reference.length || heard.length > 200 || reference.length > 120) return { tokens: heard, slips: [] };
  const n = heard.length;
  const m = reference.length;
  const known = new Set(reference);
  /** A heard word the reference already uses is never rewritten, and single letters are never joined. */
  const loose = (token) => token.length >= 2 && slipSource(token) && !known.has(token);
  const moves = (i, j) => {
    const options = [];
    if (i < n && j < m && heard[i] === reference[j]) options.push({ cost: 0, i: i + 1, j: j + 1, out: [heard[i]] });
    if (i < n && j < m && loose(heard[i]) && ((slipWord(reference[j]) && closeTo(heard[i], reference[j])) || (/^[a-z]$/.test(reference[j]) && closeToLetter(heard[i], reference[j])))) {
      options.push({ cost: SLIP_COST, i: i + 1, j: j + 1, out: [reference[j]], slip: { heard: heard[i], as: reference[j].length === 1 ? spelling(reference[j]) : reference[j] } });
    }
    for (const size of [2, 3]) {
      const joined = heard.slice(i, i + size);
      if (i + size <= n && j < m && joined.every(loose) && slipWord(reference[j]) && closeTo(joined.join(''), reference[j])) {
        options.push({ cost: SLIP_COST + size - 1, i: i + size, j: j + 1, out: [reference[j]], slip: { heard: joined.join(' '), as: reference[j] } });
      }
    }
    if (i < n && j + 1 < m && heard[i].length >= 4 && loose(heard[i]) && splitWord(reference[j]) && splitWord(reference[j + 1]) && closeTo(heard[i], reference[j] + reference[j + 1]) && editDistance(heard[i], reference[j] + reference[j + 1]) < Math.min(editDistance(heard[i], reference[j]), editDistance(heard[i], reference[j + 1]))) {
      options.push({ cost: SLIP_COST, i: i + 1, j: j + 2, out: [reference[j], reference[j + 1]], slip: { heard: heard[i], as: `${reference[j]} ${reference[j + 1]}` } });
    }
    if (i < n) options.push({ cost: SLIP_COST, i: i + 1, j, out: [heard[i]] });
    if (j < m) options.push({ cost: SLIP_COST, i, j: j + 1, out: [] });
    return options;
  };
  const best = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n; i >= 0; i -= 1) for (let j = m; j >= 0; j -= 1) if (i < n || j < m) best[i][j] = Math.min(...moves(i, j).map((move) => move.cost + best[move.i][move.j]));
  const out = [];
  const slips = [];
  for (let i = 0, j = 0; i < n || j < m;) {
    const move = moves(i, j).find((option) => option.cost + best[option.i][option.j] === best[i][j]);
    out.push(...move.out);
    if (move.slip && !slips.some((slip) => slip.heard === move.slip.heard && slip.as === move.slip.as)) slips.push(move.slip);
    i = move.i;
    j = move.j;
  }
  return { tokens: slips.length ? numberContext(out) : out, slips };
}

function callsignObservations(tokens, rubric) {
  const found = [];
  for (let i = 0; i < tokens.length; i += 1) {
    if (tokens[i] !== 'cessna') continue;
    const letters = [];
    let next = i + 1;
    while (/^[a-z]$/.test(tokens[next] ?? '')) letters.push(tokens[next++]);
    found.push({ value: `cessna ${letters.join(' ')}`.trim(), index: i, length: 1 + letters.length });
  }
  for (let i = 0; i < tokens.length; i += 1) {
    if (tokens[i] !== 'starwalker') continue;
    found.push(/^\d+$/.test(tokens[i + 1] ?? '') ? { value: `starwalker ${tokens[i + 1]}`, index: i, length: 2 } : { value: 'starwalker', index: i, length: 1 });
  }
  // An additional NATO-letter callsign must not hide behind a correct callsign.
  for (let i = 0; i < tokens.length - 2; i += 1) {
    if (tokens[i - 1] === 'cessna' || /^[a-z]$/.test(tokens[i - 1] ?? '')) continue;
    if (tokens.slice(i, i + 3).every((token) => /^[a-z]$/.test(token)) && !/^[a-z]$/.test(tokens[i + 3] ?? '')) {
      const value = tokens.slice(i, i + 3).join(' ');
      const waypointAliases = [rubric?.fields?.waypoint, ...(rubric?.aliases?.waypoint ?? [])].filter(Boolean).map((alias) => normalizeTranscript(alias).join(' '));
      if (!waypointAliases.includes(value) && !WAYPOINT_SEQUENCES.some((choice) => choice.tokens.join(' ') === value)) found.push({ value: `cessna ${value}`, index: i, length: 3 });
    }
  }
  return found;
}

function runwayObservations(tokens) {
  const found = [];
  for (let i = 0; i < tokens.length; i += 1) {
    if (!/^\d{1,2}$/.test(tokens[i])) continue;
    const side = tokens[i + 1] === 'left' ? 'l' : tokens[i + 1] === 'right' ? 'r' : tokens[i + 1] === 'center' ? 'c' : '';
    if (tokens[i - 1] === 'runway' || side) found.push({ value: `${tokens[i].padStart(2, '0')}${side}`, index: i, length: side ? 2 : 1 });
  }
  for (let i = 0; i < tokens.length; i += 1) {
    if (['left', 'right', 'center'].includes(tokens[i]) && !/^\d{1,2}$/.test(tokens[i - 1] ?? '')) {
      found.push({ value: `unspecified-${tokens[i]}`, index: i, length: 1 });
    }
  }
  return found;
}

function fieldObservations(kind, tokens, rubric) {
  if (kind === 'callsign') return callsignObservations(tokens, rubric);
  if (kind === 'runway') return runwayObservations(tokens);
  if (kind === 'direction') return tokens.flatMap((token, index) => ['left', 'right'].includes(token) ? [{ value: token, index, length: 1 }] : []);
  if (kind === 'heading') return tokens.flatMap((token, index) => tokens[index - 1] === 'heading' && /^\d+$/.test(token) ? [{ value: String(Number(token)), index, length: 1 }] : []);
  if (kind === 'waypoint') return WAYPOINT_SEQUENCES.flatMap((choice) => { const index = findSequence(tokens, choice.tokens); return index < 0 ? [] : [{ value: choice.key, index, length: choice.tokens.length }]; });
  return tokens.flatMap((token, index) => kind === 'frequency' ? (/^\d+\.\d+$/.test(token) ? [{ value: token, index, length: 1 }] : []) : (/^\d{3,5}$/.test(token) ? [{ value: token, index, length: 1 }] : []));
}

function fieldValue(kind, value) {
  return kind === 'waypoint' ? waypointKey(value) : kind === 'callsign' ? normalizeTranscript(value).join(' ') : String(value).toLowerCase();
}

function instructions(tokens) {
  const value = tokens.join(' ');
  return [
    ['takeoff-clearance', /\bcleared (?:for|to) takeoff\b/],
    ['landing-clearance', /\bcleared (?:to land|for landing)\b/],
    ['visual-approach-clearance', /\bcleared visual approach\b/],
    ['hold-short', /\bhold(?:ing)? short\b/],
    ['climb', /\bclimb\b/], ['descend', /\bdescend\b/],
    ['taxi', /\btaxi\b/], ['contact', /\bcontact\b/],
    ['abort', /\babort\b/], ['wait', /\b(?:wait|standby)\b/],
  ].filter(([, pattern]) => pattern.test(value)).map(([id]) => id);
}

function unavailable(turn, reason = 'No usable transcript was received. Please try this turn again.') {
  return { status: 'unavailable', correct: false, score: 0, criteria: [{ id: 'transcript', label: 'Transcript available', passed: false, expected: 'A complete spoken transcript', actual: '', feedback: reason }], feedback: reason, expected: turn?.expected ?? '', version: RUBRIC_VERSION };
}

function result(criteria, expected) {
  const correct = criteria.every((criterion) => criterion.passed);
  const failed = criteria.filter((criterion) => !criterion.passed);
  return {
    status: correct ? 'correct' : 'retry', correct,
    score: criteria.length ? Math.round(criteria.filter((criterion) => criterion.passed).length / criteria.length * 100) : 0,
    criteria, feedback: correct ? 'Correct readback. All required fields and the phrase order match this training script.' : failed.map((criterion) => criterion.feedback).join(' '),
    expected, version: RUBRIC_VERSION,
  };
}

function excludedIndexes(tokens, rubric) {
  const excluded = new Set();
  for (const entry of callsignObservations(tokens, rubric)) if (entry.value.startsWith('starwalker ')) for (let offset = 0; offset < entry.length; offset += 1) excluded.add(entry.index + offset);
  for (const phrase of rubric.optionalPhrases ?? []) {
    const sequence = normalizeTranscript(phrase);
    if (sequence.length) tokens.forEach((_, index) => { if (sequence.every((token, offset) => tokens[index + offset] === token)) sequence.forEach((__, offset) => excluded.add(index + offset)); });
  }
  return excluded;
}

export function evaluateTranscript(transcript, turn) {
  if (!turn?.rubric || typeof transcript !== 'string' || !/[a-z0-9]/i.test(transcript)) return unavailable(turn);
  const heard = normalizeTranscript(transcript);
  if (!heard.length) return unavailable(turn);
  const expectedTokens = normalizeTranscript(turn.expected);
  const { tokens, slips } = reconcileSlips(heard, expectedTokens);
  const excluded = excludedIndexes(tokens, turn.rubric);
  const observe = (kind) => fieldObservations(kind, tokens, turn.rubric).filter((entry) => !(['altitude', 'frequency', 'heading', 'runway', 'direction'].includes(kind) && excluded.has(entry.index)));
  const criteria = [];
  for (const [kind, value] of Object.entries(turn.rubric.fields ?? {})) {
    const expected = fieldValue(kind, value);
    const aliases = [expected, ...(turn.rubric.aliases?.[kind] ?? []).map((alias) => fieldValue(kind, alias))];
    const observed = observe(kind);
    const passed = observed.length > 0 && observed.every((entry) => aliases.includes(entry.value)) && (kind !== 'callsign' || observed.some((entry) => ['cessna', 'starwalker'].includes(tokens[entry.index])));
    const label = { callsign: 'Aircraft callsign', runway: 'Runway number and side', frequency: 'Radio frequency', altitude: 'Altitude', waypoint: 'Waypoint', direction: 'Turn direction', heading: 'Heading' }[kind] ?? kind;
    criteria.push({ id: kind, label, passed, expected: String(value), actual: observed.map((entry) => entry.value).join(', ') || 'Missing', feedback: passed ? `${label} matches.` : `Use ${label.toLowerCase()} ${value} exactly; include every required part and remove conflicting values.` });
  }
  for (const phrase of turn.rubric.phrases ?? []) {
    const passed = findSequence(tokens, normalizeTranscript(phrase.value)) >= 0;
    criteria.push({ id: phrase.id, label: phrase.label, passed, expected: phrase.value, actual: passed ? phrase.value : 'Missing or changed', feedback: passed ? `${phrase.label} matches.` : `Say “${phrase.value}” as shown in the training phrase.` });
  }
  const unexpectedFields = ['runway', 'frequency', 'altitude'].filter((kind) => tokens.includes(kind) && !(kind in (turn.rubric.fields ?? {})));
  criteria.push({ id: 'field-conflict', label: 'Only required flight information', passed: unexpectedFields.length === 0, expected: Object.keys(turn.rubric.fields ?? {}).join(', '), actual: unexpectedFields.join(', ') || 'No extra flight fields', feedback: unexpectedFields.length ? `Remove the additional ${unexpectedFields.join(', ')} information. Use only the displayed training phrase.` : 'No extra flight information was added.' });
  const forbidden = (turn.rubric.forbiddenPhrases ?? []).filter((value) => { const sequence = normalizeTranscript(value); return sequence.length && findSequence(tokens, sequence) >= 0; });
  criteria.push({ id: 'emergency-conflict', label: 'Only the reported emergency', passed: forbidden.length === 0, expected: 'Only the displayed emergency', actual: forbidden.join(', ') || 'No other emergency', feedback: forbidden.length ? 'Report only the displayed emergency.' : 'Only the displayed emergency is reported.' });
  const permittedNegativePositions = new Set();
  for (const value of turn.rubric.allowedNegativePhrases ?? []) {
    const sequence = normalizeTranscript(value);
    const index = findSequence(tokens, sequence);
    if (index >= 0) sequence.forEach((_, offset) => permittedNegativePositions.add(index + offset));
  }
  const negatives = tokens.filter((token, index) => NEGATIVES.has(token) && !permittedNegativePositions.has(index));
  criteria.push({ id: 'polarity', label: 'Instruction meaning', passed: negatives.length === 0, expected: 'No added negative or cancellation', actual: negatives.join(', ') || 'Positive statement', feedback: negatives.length ? 'An added negative or cancellation changes the instruction. Retry without changing its meaning.' : 'Instruction meaning is preserved.' });
  const expectedInstructions = instructions(expectedTokens);
  const actualInstructions = instructions(tokens);
  const unexpectedInstructions = actualInstructions.filter((instruction) => !expectedInstructions.includes(instruction));
  criteria.push({ id: 'instruction-conflict', label: 'No conflicting instruction', passed: unexpectedInstructions.length === 0, expected: expectedInstructions.join(', ') || 'The displayed request', actual: actualInstructions.join(', ') || 'No clearance or movement instruction', feedback: unexpectedInstructions.length ? 'Remove the extra clearance or movement instruction. Read back only the displayed training instruction.' : 'No conflicting instruction was added.' });
  const expectedNumbers = new Set(expectedTokens.filter((token) => /^\d+(?:\.\d+)?$/.test(token)));
  const unexpectedNumbers = tokens.filter((token, index) => /^\d+(?:\.\d+)?$/.test(token) && !excluded.has(index) && !expectedNumbers.has(token));
  criteria.push({ id: 'number-conflict', label: 'No conflicting numbers', passed: unexpectedNumbers.length === 0, expected: [...expectedNumbers].join(', ') || 'No numbers in this phrase', actual: tokens.filter((token) => /^\d+(?:\.\d+)?$/.test(token)).join(', ') || 'No numbers', feedback: unexpectedNumbers.length ? `Remove or correct the conflicting number: ${unexpectedNumbers.join(', ')}.` : 'All spoken numbers agree with the displayed phrase.' });
  let end = -1;
  const orderParts = [];
  let correctOrder = true;
  for (const segment of turn.rubric.order ?? []) {
    const value = fieldValue(segment.kind, segment.value);
    const allowedValues = [value, ...(turn.rubric.aliases?.[segment.kind] ?? []).map((alias) => fieldValue(segment.kind, alias))];
    const observation = segment.kind === 'phrase' ? (() => { const sequence = normalizeTranscript(segment.value); return { index: findSequence(tokens, sequence), length: sequence.length }; })() : observe(segment.kind).find((entry) => allowedValues.includes(entry.value));
    const position = observation?.index ?? -1;
    if (position < 0 || position <= end) correctOrder = false;
    if (position >= 0) end = position + observation.length - 1;
    orderParts.push(segment.label);
  }
  criteria.push({ id: 'phrase-order', label: 'Training phrase order', passed: correctOrder, expected: orderParts.join(' → '), actual: correctOrder ? 'Matches' : 'Missing or out of order', feedback: correctOrder ? 'Phrase order matches.' : `Use this order: ${orderParts.join(' → ')}.` });
  return slips.length ? { ...result(criteria, turn.expected), slips } : result(criteria, turn.expected);
}

/** Tower lines may repeat the callsign and say feet after a number; every other token stays strict. Takes normalised tokens. */
function towerTokens(heard, callsign) {
  const tokens = canonical(heard);
  const copyAt = (index) => callsign.length > 0 && callsign.every((token, offset) => tokens[index + offset] === token);
  const out = [];
  for (let i = 0; i < tokens.length;) {
    if (copyAt(i) && copyAt(i + callsign.length)) { out.push(...callsign); while (copyAt(i)) i += callsign.length; }
    else if (['feet', 'ft'].includes(tokens[i]) && /^\d+(?:\.\d+)?$/.test(tokens[i - 1] ?? '')) i += 1;
    else out.push(tokens[i++]);
  }
  return out;
}

/** ATC speech must be the fixed scenario line, with only equivalent transcription spelling. */
export function evaluateTowerTranscript(transcript, turn) {
  if (!turn?.atcText || typeof transcript !== 'string' || !/[a-z0-9]/i.test(transcript)) return unavailable({ expected: turn?.atcText });
  const callsign = normalizeTranscript(turn.rubric?.fields?.callsign);
  const reference = normalizeTranscript(turn.atcText);
  const actual = towerTokens(reconcileSlips(normalizeTranscript(transcript), reference).tokens, callsign).join(' ');
  const expected = towerTokens(reference, callsign).join(' ');
  return result([{ id: 'fixed-atc-line', label: 'Fixed ATC training line', passed: actual === expected, expected: turn.atcText, actual: transcript, feedback: actual === expected ? 'ATC used the fixed scenario line.' : 'ATC speech differed from the fixed scenario. Do not use this instruction; retry the turn with the displayed ATC line.' }], turn.atcText);
}
